import React, { useState, useRef, useEffect, useCallback } from "react";
import "./AudioPlayerCustom.scss";
import { FaPlay } from "react-icons/fa6";
import { FaPause } from "react-icons/fa6";

const activePlayers = new Set();

const pauseOtherPlayers = (currentStop) => {
  activePlayers.forEach((stop) => {
    if (stop !== currentStop) {
      stop();
    }
  });
};

const AudioPlayerCustom = ({ audioUrl }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState("0:00");
  const [duration, setDuration] = useState("0:00");
  const [progress, setProgress] = useState(0);

  const stopPlayback = useCallback(() => {
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      audio.pause();
    }
    setIsPlaying(false);
  }, []);

  useEffect(() => {
    activePlayers.add(stopPlayback);
    return () => {
      activePlayers.delete(stopPlayback);
      stopPlayback();
    };
  }, [stopPlayback]);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      pauseOtherPlayers(stopPlayback);
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch((error) => console.error("Error playing audio:", error));
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio?.duration) return;
    const nextProgress = (audio.currentTime / audio.duration) * 100;
    setProgress(nextProgress);
    setCurrentTime(formatTime(audio.currentTime));
  };

  const handleProgressBarChange = (e) => {
    const audio = audioRef.current;
    if (!audio?.duration) return;
    const newTime = (e.target.value / 100) * audio.duration;
    audio.currentTime = newTime;
    setProgress(e.target.value);
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime("0:00");
  };

  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (audio?.duration) {
      setDuration(formatTime(audio.duration));
    }
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  return (
    <div
      className="audio-player"
      style={{ display: "flex", alignItems: "center", width: "100%" }}
    >
      <button className="play-pause-btn" onClick={handlePlayPause}>
        {isPlaying ? <FaPause /> : <FaPlay />}
      </button>
      <input
        type="range"
        className="progress-bar"
        value={progress}
        onChange={handleProgressBarChange}
        style={{ flex: 1, margin: "0px 5px 0px 8px" }}
      />
      <span className="time-display">{currentTime} / {duration}</span>
      <audio
        className="audio-element"
        src={audioUrl}
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleAudioEnded}
        onLoadedMetadata={handleLoadedMetadata}
        onPause={handlePause}
        onPlay={handlePlay}
      />
    </div>
  );
};
export default AudioPlayerCustom;
